import React, { useId, useMemo, useState } from 'react';
import {
  avatarColour,
  formatDateTime,
  formatDay,
  formatDuration,
  formatTime,
  initialsOf,
  plural,
  timeAgo,
  toDate,
} from './utils/visitorStatsHelpers';
import './VisitorStatsTabs.css';

const PAGE_SIZE = 25;
const SORTS = [
  { id: 'recent', label: 'Most recent activity', value: (u) => toDate(u.lastSeen)?.getTime() || 0 },
  { id: 'pages', label: 'Most pages viewed', value: (u) => u.totalPages || 0 },
  { id: 'interactions', label: 'Most interactions', value: (u) => u.interactions?.length || 0 },
  { id: 'sessions', label: 'Most sessions', value: (u) => u.sessions?.length || 0 },
];

const INTERACTION_META = {
  link_click: { icon: '🔗', label: 'Link click' },
  popup_open: { icon: '📦', label: 'Popup opened' },
  audio_play: { icon: '🎵', label: 'Audio played' },
};

const displayName = (user) => (user.name && user.name !== 'No name' ? user.name : user.email);

function ShowMore({ shown, total, step, onMore }) {
  if (shown >= total) return null;
  return (
    <button type="button" className="vs-more" onClick={() => onMore(shown + step)}>
      Show {Math.min(step, total - shown)} more <span>({total - shown} hidden)</span>
    </button>
  );
}

function PagesSection({ user }) {
  const [shown, setShown] = useState(10);
  const visits = useMemo(() => user.pageVisits || [], [user.pageVisits]);
  const top = useMemo(() => {
    const counts = {};
    visits.forEach((v) => { counts[v.page] = (counts[v.page] || 0) + 1; });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  }, [visits]);

  if (visits.length === 0) return <p className="vs-empty">No page views recorded in this period.</p>;

  return (
    <>
      <h4 className="vs-sub-title">Most visited</h4>
      <ul className="vs-bars">
        {top.map(([page, count]) => (
          <li key={page}>
            <span className="vs-bars-label">{page}</span>
            <span className="vs-bars-count">{count}×</span>
            <span className="vs-bars-track"><span style={{ width: `${(count / top[0][1]) * 100}%` }} /></span>
          </li>
        ))}
      </ul>

      <h4 className="vs-sub-title">Recent visits ({visits.length})</h4>
      <ul className="vs-timeline">
        {visits.slice(0, shown).map((v, i) => (
          <li key={i}>
            <time className="vs-timeline-time">{formatDateTime(v.timestamp)}</time>
            <span className="vs-timeline-text vs-path">{v.page}</span>
          </li>
        ))}
      </ul>
      <ShowMore shown={shown} total={visits.length} step={20} onMore={setShown} />
    </>
  );
}

function InteractionsSection({ user }) {
  const [shown, setShown] = useState(10);
  const items = user.interactions || [];
  if (items.length === 0) return <p className="vs-empty">No button clicks, popups or audio plays recorded.</p>;

  return (
    <>
      <ul className="vs-timeline">
        {items.slice(0, shown).map((i, idx) => {
          const meta = INTERACTION_META[i.type] || { icon: '👆', label: 'Interaction' };
          const d = i.details || {};
          return (
            <li key={idx}>
              <time className="vs-timeline-time">{formatDateTime(i.timestamp)}</time>
              <span className="vs-timeline-text">
                <strong>{meta.icon} {meta.label}</strong>
                {' — '}
                {d.button || d.popup || d.track || '—'}
                {d.competition && <span className="vs-muted"> · {d.competition}</span>}
              </span>
            </li>
          );
        })}
      </ul>
      <ShowMore shown={shown} total={items.length} step={20} onMore={setShown} />
    </>
  );
}

function SessionsSection({ user }) {
  const [shown, setShown] = useState(5);
  const sessions = user.sessions || [];
  if (sessions.length === 0) return <p className="vs-empty">No sessions recorded (older visits did not store a session id).</p>;

  return (
    <>
      {sessions.slice(0, shown).map((s) => (
        <details key={s.sessionId} className="vs-session">
          <summary>
            <span className="vs-session-when">{formatDay(s.startTime)}</span>
            <span className="vs-session-meta">
              {formatTime(s.startTime)}–{formatTime(s.lastActivity)}
              {formatDuration(s.startTime, s.lastActivity) && ` · ${formatDuration(s.startTime, s.lastActivity)}`}
              {` · ${plural(s.pages?.length || 0, 'page')}`}
            </span>
          </summary>
          <ol className="vs-timeline vs-timeline--numbered">
            {(s.pages || []).map((p, i) => (
              <li key={i}>
                <time className="vs-timeline-time">{formatTime(p.timestamp)}</time>
                <span className="vs-timeline-text vs-path">{p.page}</span>
              </li>
            ))}
          </ol>
        </details>
      ))}
      <ShowMore shown={shown} total={sessions.length} step={10} onMore={setShown} />
    </>
  );
}

function UserCard({ user }) {
  const [open, setOpen] = useState(false);
  const [section, setSection] = useState('pages');
  const panelId = useId();
  const name = displayName(user);
  const interactionCount = user.interactions?.length || 0;
  const sessionCount = user.sessions?.length || 0;

  const sections = [
    { id: 'pages', label: 'Pages', count: user.pageVisits?.length || 0 },
    { id: 'interactions', label: 'Interactions', count: interactionCount },
    { id: 'sessions', label: 'Sessions', count: sessionCount },
  ];

  return (
    <article className={`vs-user ${open ? 'is-open' : ''}`}>
      <button type="button" className="vs-user-head" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((o) => !o)}>
        <span className="vs-avatar" style={{ background: avatarColour(user.email) }} aria-hidden="true">
          {initialsOf(user.name, user.email)}
        </span>
        <span className="vs-user-id">
          <span className="vs-user-name">{name}</span>
          {name !== user.email && <span className="vs-user-email">{user.email}</span>}
        </span>
        <svg className="vs-chev" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" focusable="false">
          <path d="M5 7.5l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="vs-chips">
          <span className="vs-chip" title={formatDateTime(user.lastSeen)}>🕐 {timeAgo(user.lastSeen)}</span>
          <span className="vs-chip">📄 {plural(user.totalPages || 0, 'page')}</span>
          <span className="vs-chip">👆 {plural(interactionCount, 'interaction')}</span>
          <span className="vs-chip">🗂️ {plural(sessionCount, 'session')}</span>
        </span>
      </button>

      {open && (
        <div className="vs-user-body" id={panelId}>
          <p className="vs-user-dates">
            First seen <strong>{formatDateTime(user.firstSeen)}</strong> · Last seen <strong>{formatDateTime(user.lastSeen)}</strong>
          </p>
          <div className="vs-segmented" role="tablist">
            {sections.map((s) => (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={section === s.id}
                className={section === s.id ? 'active' : ''}
                onClick={() => setSection(s.id)}
              >
                {s.label} <span>{s.count}</span>
              </button>
            ))}
          </div>
          <div role="tabpanel">
            {section === 'pages' && <PagesSection user={user} />}
            {section === 'interactions' && <InteractionsSection user={user} />}
            {section === 'sessions' && <SessionsSection user={user} />}
          </div>
        </div>
      )}
    </article>
  );
}

export default function VisitorUsersTab({ users }) {
  const [search, setSearch] = useState('');
  const [sortId, setSortId] = useState('recent');
  const [shown, setShown] = useState(PAGE_SIZE);

  const totals = useMemo(() => {
    const dayAgo = Date.now() - 24 * 60 * 60 * 1000;
    return {
      pages: users.reduce((n, u) => n + (u.totalPages || 0), 0),
      interactions: users.reduce((n, u) => n + (u.interactions?.length || 0), 0),
      activeToday: users.filter((u) => (toDate(u.lastSeen)?.getTime() || 0) >= dayAgo).length,
    };
  }, [users]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sort = SORTS.find((s) => s.id === sortId) || SORTS[0];
    return users
      .filter((u) => !q || `${u.name || ''} ${u.email || ''}`.toLowerCase().includes(q))
      .sort((a, b) => sort.value(b) - sort.value(a));
  }, [users, search, sortId]);

  return (
    <div className="stats-section vs-tab">
      <h2>👤 Signed-in users</h2>

      <div className="vs-kpis">
        <div className="vs-kpi"><b>{users.length.toLocaleString()}</b><span>Users</span></div>
        <div className="vs-kpi"><b>{totals.activeToday.toLocaleString()}</b><span>Active in last 24 h</span></div>
        <div className="vs-kpi"><b>{totals.pages.toLocaleString()}</b><span>Pages viewed</span></div>
        <div className="vs-kpi"><b>{totals.interactions.toLocaleString()}</b><span>Interactions</span></div>
      </div>

      <div className="vs-toolbar">
        <input
          type="search"
          className="vs-input"
          placeholder="Search name or e-mail…"
          aria-label="Search users"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setShown(PAGE_SIZE); }}
        />
        <select className="vs-input vs-select" aria-label="Sort users" value={sortId} onChange={(e) => setSortId(e.target.value)}>
          {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      <p className="vs-result-count">{plural(list.length, 'user')}{search && ` matching “${search}”`}</p>

      <div className="vs-user-list">
        {list.slice(0, shown).map((u) => <UserCard key={u.email} user={u} />)}
        {list.length === 0 && (
          <div className="vs-empty-box">
            {users.length === 0 ? 'No signed-in user activity recorded in this period.' : 'No users match your search.'}
          </div>
        )}
      </div>
      <ShowMore shown={shown} total={list.length} step={PAGE_SIZE} onMore={setShown} />
    </div>
  );
}
